/**
 * CRM Drag and Drop Hook
 *
 * Manages drag/drop state and handlers for the Pipeline Kanban board
 */

import { useState, useCallback } from 'react';
import {
  DragEndEvent,
  DragStartEvent,
  PointerSensor,
  KeyboardSensor,
  type SensorDescriptor,
  type SensorOptions,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { toast } from 'sonner';
import { useMoveOpportunityStage } from './useCrmPipeline';
import { getStageDefinition, isTransitionAllowed } from '../constants';
import type { PipelineOpportunity, PipelineColumn, PipelineStage } from '../types';

// Plain sensor descriptors keep CRM drag/drop working without going through
// dnd-kit's sensor hooks, which are crashing in the current dev runtime.
const CRM_DND_SENSORS = [
  {
    sensor: PointerSensor,
    options: {
      activationConstraint: {
        distance: 8,
      },
    },
  },
  {
    sensor: KeyboardSensor,
    options: {
      coordinateGetter: sortableKeyboardCoordinates,
    },
  },
] satisfies SensorDescriptor<SensorOptions>[];

interface UseCrmDragDropOptions {
  columns: PipelineColumn[];
  onQuoteTransform?: (opportunity: PipelineOpportunity) => void;
}

interface UseCrmDragDropResult {
  sensors: SensorDescriptor<SensorOptions>[];
  activeOpportunity: PipelineOpportunity | null;
  isMoving: boolean;
  handleDragStart: (event: DragStartEvent) => void;
  handleDragEnd: (event: DragEndEvent) => void;
}

/**
 * Hook for managing drag/drop functionality on the CRM Pipeline Kanban board
 */
export function useCrmDragDrop({
  columns,
  onQuoteTransform,
}: UseCrmDragDropOptions): UseCrmDragDropResult {
  const moveOpportunityMutation = useMoveOpportunityStage();
  const [activeOpportunity, setActiveOpportunity] = useState<PipelineOpportunity | null>(null);

  const sensors = CRM_DND_SENSORS;

  // Handle drag start
  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const opportunityId = event.active.id as string;

      // Find the opportunity across all columns
      let foundOpportunity: PipelineOpportunity | null = null;
      for (const column of columns) {
        const opportunity = column.opportunities.find((o) => o.id === opportunityId);
        if (opportunity) {
          foundOpportunity = opportunity;
          break;
        }
      }

      setActiveOpportunity(foundOpportunity);
    },
    [columns]
  );

  // Handle drag end
  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      setActiveOpportunity(null);

      if (!over) return;

      const opportunityId = active.id as string;
      const targetStageKey = over.id as PipelineStage;

      // Find the target stage definition
      const targetStageDef = getStageDefinition(targetStageKey);
      if (!targetStageDef) return;

      // Find the source opportunity
      let sourceOpportunity: PipelineOpportunity | undefined;
      for (const column of columns) {
        const found = column.opportunities.find((o) => o.id === opportunityId);
        if (found) {
          sourceOpportunity = found;
          break;
        }
      }

      if (!sourceOpportunity) return;

      // Don't do anything if dropping on the same stage
      if (sourceOpportunity.stage === targetStageKey) return;

      // Check if this is a terminal stage that transforms to quote
      if (targetStageDef.transformsTo === 'quote') {
        // Instead of moving, trigger the quote transformation modal
        if (onQuoteTransform) {
          onQuoteTransform(sourceOpportunity);
        }
        return;
      }

      // Validate stage transition is allowed
      const sourceStageDef = getStageDefinition(sourceOpportunity.stage);
      if (!isTransitionAllowed(sourceOpportunity.stage, targetStageKey)) {
        toast.error('Invalid stage transition', {
          description: `Cannot move from "${sourceStageDef?.label || sourceOpportunity.stage}" to "${targetStageDef.label}". Please follow the sales process stages.`,
        });
        return;
      }

      // Regular stage move
      moveOpportunityMutation.mutate({
        accountId: sourceOpportunity.account_id,
        opportunityId: sourceOpportunity.id,
        input: {
          opportunity_id: sourceOpportunity.id,
          target_stage: targetStageKey,
        },
      });
    },
    [columns, moveOpportunityMutation, onQuoteTransform]
  );

  return {
    sensors,
    activeOpportunity,
    isMoving: moveOpportunityMutation.isPending,
    handleDragStart,
    handleDragEnd,
  };
}
